import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { Fragment } from 'react'

interface CancellationSuccessModalProps {
  open: boolean
  onClose: () => void
  userRole: 'client' | 'professional'
  refundAmount?: string
  compensationAmount?: string
  message?: string
}

export default function CancellationSuccessModal({
  open,
  onClose,
  userRole,
  refundAmount,
  compensationAmount,
  message
}: CancellationSuccessModalProps) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 scale-[0.985]" enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-[440px] overflow-hidden rounded-2xl relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                <div className="p-6 md:p-8 text-center">
                  {/* Icon */}
                  <div className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-5"
                    style={{ background: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
                  </div>

                  <h3 className="text-[22px] font-medium tracking-tight">Meeting <span className="font-serif italic font-normal">cancelled</span></h3>
                  <p className="text-white/50 text-[13px] mt-2 max-w-sm mx-auto">
                    Your meeting has been successfully cancelled.
                  </p>

                  {/* Refund info (client) */}
                  {userRole === 'client' && refundAmount && refundAmount !== '$0.00' && (
                    <div className="rounded-xl p-4 mt-5 text-left" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <div className="text-[11px] tracking-[2px] uppercase text-white/50 mb-1.5">Refund</div>
                      <div className="text-[24px] font-semibold tracking-tight">{refundAmount}</div>
                      <p className="text-white/50 text-[11.5px] mt-1">
                        Refunds typically appear in 5-10 business days.
                      </p>
                    </div>
                  )}

                  {userRole === 'client' && refundAmount === '$0.00' && (
                    <div className="rounded-xl p-4 mt-5 text-left" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <p className="text-white/50 text-[12.5px]">
                        No refund available due to the cancellation timing.
                      </p>
                    </div>
                  )}

                  {/* Compensation info (professional) */}
                  {userRole === 'professional' && compensationAmount && parseFloat(compensationAmount.replace('$', '')) > 0 && (
                    <div className="rounded-xl p-4 mt-5 text-left" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <div className="text-[11px] tracking-[2px] uppercase text-white/50 mb-1.5">Compensation</div>
                      <div className="text-[24px] font-semibold tracking-tight">{compensationAmount}</div>
                      <p className="text-white/50 text-[11.5px] mt-1">
                        Will be transferred to your Stripe account after 7-day hold period.
                      </p>
                    </div>
                  )}

                  {userRole === 'professional' && (!compensationAmount || parseFloat(compensationAmount.replace('$', '')) === 0) && (
                    <div className="rounded-xl p-4 mt-5 text-left" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <p className="text-white/50 text-[12.5px]">
                        The client will receive a full refund for this cancellation.
                      </p>
                    </div>
                  )}

                  {/* Additional message */}
                  {message && (
                    <div className="rounded-xl p-3.5 mt-4 text-left flex items-start gap-2.5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid hsl(0 0% 20% / 0.5)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/50 shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      <p className="text-white/60 text-[12.5px] leading-relaxed">{message}</p>
                    </div>
                  )}

                  {/* Close button */}
                  <button
                    onClick={onClose}
                    className="btn btn-primary w-full justify-center !py-3 mt-6"
                  >
                    Done
                  </button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
